if (typeof SearchForm !== 'undefined') {
  class PredictiveSearch extends SearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances =
        document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      // FAQ dans la recherche (2026-09-02) : réglage lu sur l'élément (data-show-faq),
      // posé par la section qui rend <predictive-search> (header ou main-search).
      this.faqEnabled = this.dataset.showFaq === 'true' && typeof FaqSearchIndex !== 'undefined';
      this.faqHeading = this.dataset.faqHeading || 'FAQ';
  
      this.setupEventListeners();
    }
  
    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
  
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }
  
    getQuery() {
      return this.input.value.trim();
    }
  
    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        // Remove the results when they are no longer relevant for the new search term
        // so they don't show up when the dropdown opens again
        this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
      }
  
      // Update the term asap, don't wait for the predictive search query to finish loading
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
  
      this.searchTerm = newSearchTerm;
  
      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }
  
      this.getSearchResults(this.searchTerm);
    }
  
    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }
  
    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }
  
    onFocus() {
      // Premier focus : on précharge l'index FAQ (une requête par visite, cf. faq-search-index.js)
      this.preloadFaqIndex();
      const currentSearchTerm = this.getQuery();
  
      if (!currentSearchTerm.length) return;
  
      if (this.searchTerm !== currentSearchTerm) {
        // Search term was changed from other search input, treat it as a user change
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }
  
    onFocusOut() {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      })
    }
  
    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();
  
      switch (event.code) {
        case 'ArrowUp':
          this.switchOption('up')
          break;
        case 'ArrowDown':
          this.switchOption('down');
          break;
        case 'Enter':
          this.selectOption();
          break;
      }
    }
  
    onKeydown(event) {
      // Prevent the cursor from moving in the input when using the up and down arrow keys
      if (
        event.code === 'ArrowUp' ||
        event.code === 'ArrowDown'
      ) {
        event.preventDefault();
      }
    }
  
    updateSearchForTerm(previousTerm, newTerm) {
      const searchForTextElement = this.querySelector(
        "[data-predictive-search-search-for-text]"
      );
      const currentButtonText = searchForTextElement?.innerText;
      if (currentButtonText) {
        if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
          // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
          return;
        }
        const newButtonText = currentButtonText.replace(previousTerm, newTerm);
        searchForTextElement.innerText = newButtonText;
      }
    }
  
    switchOption(direction) {
      if (!this.getAttribute('open')) return;
  
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
  
      // Filter out hidden elements (duplicated page and article resources) thanks
      // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
      const allVisibleElements = Array.from(
        this.querySelectorAll("li, button.predictive-search__item")
      ).filter((element) => element.offsetParent !== null);
      let activeElementIndex = 0;
  
      if (moveUp && !selectedElement) return;
  
      let selectedElementIndex = -1;
      let i = 0;
  
      while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
        if (allVisibleElements[i] === selectedElement) {
          selectedElementIndex = i;
        }
        i++;
      }
  
      this.statusElement.textContent = "";
  
      if (!moveUp && selectedElement) {
        activeElementIndex =
          selectedElementIndex === allVisibleElements.length - 1
            ? 0
            : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex =
          selectedElementIndex === 0
            ? allVisibleElements.length - 1
            : selectedElementIndex - 1;
      }
  
      if (activeElementIndex === selectedElementIndex) return;
  
      const activeElement = allVisibleElements[activeElementIndex];
  
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);
  
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  
    selectOption() {
      const selectedOption = this.querySelector(
        '[aria-selected="true"] a, button[aria-selected="true"]'
      );
  
      if (selectedOption) selectedOption.click();
    }
  
    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(" ", "-").toLowerCase();
      this.setLiveRegionLoadingState();
  
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }
  
      fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[options][unavailable_products]=hide&resources[options][fields]=title,variants.sku,product_type,variants.title&section_id=predictive-search`,
      { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            this.close();
            throw error;
          }
  
          return response.text();
        })
        .then((text) => {
          const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach(
            (predictiveSearchInstance) => {
              predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
            }
          );
          this.renderSearchResults(resultsMarkup);
        })
        .catch((error) => {
          if (error?.code === 20) {
            // Code 20 means the call was aborted
            return;
          }
          this.close();
          throw error;
        });
    }
  
    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
  
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', true);
    }
  
    setLiveRegionText(statusText) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;
  
      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  
    renderSearchResults(resultsMarkup) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
  
      this.setLiveRegionResults();
      this.open();
      this.injectFaqResults();
    }

    preloadFaqIndex() {
      if (this.faqEnabled) FaqSearchIndex.load();
    }

    // Ajoute un groupe « Questions fréquentes » (5 max) sous les produits, à partir de
    // l'index des métaobjets faq_entry (cf. sections/faq-search-index.liquid).
    // Même markup/classes que les groupes rendus par sections/predictive-search.liquid,
    // donc navigables au clavier comme eux (role=option + aria-selected).
    // Depuis le 2026-09-03 l'index porte la réponse complète : chaque résultat montre
    // sous son titre un extrait (FaqSearchIndex.snippet, 18 mots) avec les mots tapés
    // en <mark>, pour justifier une question dont le titre ne contient pas le mot.
    injectFaqResults() {
      if (!this.faqEnabled) return;
      const term = this.searchTerm;
      FaqSearchIndex.load().then((entries) => {
        if (term !== this.searchTerm) return; // la saisie a changé entre-temps
        const results = this.querySelector('#predictive-search-results');
        if (!results) return;
        results.querySelectorAll('.predictive-search__faq-wrapper').forEach((el) => el.remove());

        const matches = FaqSearchIndex.match(entries, term, 5);
        if (!matches.length) return;

        let groupsWrapper = results.querySelector('#predictive-search-results-groups-wrapper');
        if (!groupsWrapper) {
          // Aucun produit : on recrée la structure « --no-products » attendue par le CSS
          groupsWrapper = document.createElement('div');
          groupsWrapper.id = 'predictive-search-results-groups-wrapper';
          groupsWrapper.className = 'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-products predictive-search__results-groups-wrapper--no-suggestions';
          const group = document.createElement('div');
          group.className = 'predictive-search__result-group';
          groupsWrapper.appendChild(group);
          const loading = results.querySelector('.predictive-search__loading-state');
          results.insertBefore(groupsWrapper, loading);
        }
        const groups = groupsWrapper.querySelectorAll(':scope > .predictive-search__result-group');
        const group = groups[groups.length - 1];
        group.appendChild(this.buildFaqGroup(matches, term));

        const countValue = this.querySelector('[data-predictive-search-live-region-count-value]');
        if (countValue && this.statusElement) {
          this.setLiveRegionText(`${countValue.textContent.trim()}, ${matches.length} ${this.faqHeading.toLowerCase()}`);
        }
      });
    }

    buildFaqGroup(matches, term) {
      const wrapper = document.createElement('div');
      wrapper.className = 'predictive-search__faq-wrapper';

      const heading = document.createElement('h2');
      heading.id = 'predictive-search-faq';
      heading.className = 'predictive-search__heading text-body caption-with-letter-spacing';
      heading.textContent = this.faqHeading;
      wrapper.appendChild(heading);

      const list = document.createElement('ul');
      list.id = 'predictive-search-results-faq-list';
      list.className = 'predictive-search__results-list list-unstyled';
      list.setAttribute('role', 'group');
      list.setAttribute('aria-labelledby', heading.id);

      matches.forEach((entry, index) => {
        const item = document.createElement('li');
        item.id = `predictive-search-option-faq-${index + 1}`;
        item.className = 'predictive-search__list-item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');

        const link = document.createElement('a');
        link.href = entry.u;
        link.className = 'predictive-search__item link link--text';
        link.setAttribute('tabindex', '-1');

        const content = document.createElement('div');
        content.className = 'predictive-search__item-content';
        if (entry.c) {
          const category = document.createElement('div');
          category.className = 'predictive-search__item-vendor caption-with-letter-spacing';
          category.textContent = entry.c;
          content.appendChild(category);
        } else {
          content.classList.add('predictive-search__item-content--centered');
        }
        const title = document.createElement('p');
        title.className = 'predictive-search__item-heading h5';
        title.textContent = entry.q;
        content.appendChild(title);

        // Extrait justificatif : fragment DOM (texte + <mark>), jamais d'innerHTML depuis la donnée.
        if (typeof FaqSearchIndex.snippet === 'function') {
          const excerpt = document.createElement('p');
          excerpt.className = 'predictive-search__item-excerpt caption';
          excerpt.appendChild(FaqSearchIndex.renderSnippet(FaqSearchIndex.snippet(entry, term, 18), document));
          if (excerpt.textContent.trim()) content.appendChild(excerpt);
        }

        link.appendChild(content);
        item.appendChild(link);
        list.appendChild(item);
      });

      wrapper.appendChild(list);
      return wrapper;
    }
  
    setLiveRegionResults() {
      this.removeAttribute('loading');
      this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
    }
  
    getResultsMaxHeight() {
      this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
      return this.resultsMaxHeight;
    }
  
    open() {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    }
  
    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }
  
    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');
  
      if (selected) selected.setAttribute('aria-selected', false);
  
      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false
      this.predictiveSearchResults.removeAttribute('style');
    }
  }
  
  customElements.define('predictive-search', PredictiveSearch);
  }
  